import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/notes");
    setNotes(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!content.trim()) return toast.error("Note content required");
    setSaving(true);
    try {
      await api.post("/notes", { title, content });
      setTitle(""); setContent("");
      toast.success("Note saved");
      load();
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/notes/${id}`);
    toast.success("Deleted");
    load();
  };

  // Group notes by date
  const grouped = notes.reduce((acc, n) => {
    const d = new Date(n.created_at).toDateString();
    (acc[d] = acc[d] || []).push(n);
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="notes-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Lab notebook</div>
        <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">Daily lab notes</h1>
        <p className="text-sm font-body text-zinc-500 mt-1">Timestamped entries for thoughts, decisions, and observations.</p>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 space-y-3">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="border-0 shadow-none focus-visible:ring-0 px-0 text-lg font-sans font-semibold bg-transparent"
          data-testid="note-title-input"
        />
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write freely. Your thoughts are saved securely."
          rows={6}
          className="border-0 shadow-none focus-visible:ring-0 px-0 resize-none bg-transparent font-body"
          data-testid="note-content-input"
        />
        <div className="flex justify-end">
          <Button onClick={add} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-note-btn">
            <Plus className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save note"}
          </Button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md p-16 text-center">
          <BookOpen className="h-8 w-8 mx-auto text-zinc-400 mb-3" />
          <div className="font-sans font-semibold">No notes yet</div>
          <div className="text-sm font-body text-zinc-500 mt-1">Start journaling your research process.</div>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([day, items]) => (
            <div key={day}>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3 sticky top-16 bg-zinc-50 dark:bg-zinc-950 py-2">
                {day}
              </div>
              <ul className="space-y-3">
                {items.map((n) => (
                  <li key={n.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5" data-testid={`note-${n.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {n.title && <div className="font-sans font-semibold mb-1">{n.title}</div>}
                        <p className="font-body text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">{n.content}</p>
                        <div className="text-xs font-mono text-zinc-500 mt-3">
                          {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => del(n.id)} className="hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
